import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces/api/requirement";
import { ISignedDocumentResponse } from "@wingspanhq/signed-documents/dist/lib/interfaces";
import { useWSQueries } from "@ws-react-query";
import { useMemo } from "react";
import { QUERY_SIGNED_DOCUMENT } from "../../../../query/signedDocuments/keys";
import { getSignedDocument } from "../../../../services/signedDocuments";
import { getDataSourceQueries } from "../../helpers/getDataSourceQueries";
import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import { buildConfigFields } from "../../utils";
import { getSignedDocumentFromCache } from "./configs/helpers/getSignedDocumentFromCache";
import { usePayeeSignatureRequirementConfig } from "./configs/payee/usePayeeSignatureRequirementConfig";
import { usePayerSignatureRequirementConfig } from "./configs/payer/usePayerSignatureRequirementConfig";

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const queries = getDataSourceQueries(
    QUERY_SIGNED_DOCUMENT,
    requirements,
    getSignedDocument,
    RequirementType.Signature
  );

  return useWSQueries({ queries });
};

export const useSignatureRequirementTableConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.Signature] => {
  const contextQueries = useContextQueries(requirements);

  const payerSignatureRequirementConfig =
    usePayerSignatureRequirementConfig(callback);
  const payeeSignatureRequirementConfig =
    usePayeeSignatureRequirementConfig(callback);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  const fields = useMemo(
    buildConfigFields<ISignedDocumentResponse, RequirementType.Signature>({
      payerConfig: payerSignatureRequirementConfig,
      payeeConfig: payeeSignatureRequirementConfig,
      getAdditionalData: getSignedDocumentFromCache
    }),
    [queriesHash]
  );

  return {
    ...fields,
    getAdditionalData(requirement, context) {
      return getSignedDocumentFromCache(
        requirement as IRequirementResponse,
        context
      );
    },
    isLoading: contextQueries.some(query => query.isLoading),
    dataUpdatedAt: queriesHash
  };
};
